import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Box,
    Button,
    Alert,
} from '@mui/material';
import { ArrowBack, ArrowForward, CallSplit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../common/PageHeader';
import PageContainer from '../common/PageContainer';
import DebundleBundleSelector from './DebundleBundleSelector';
import DebundleQuantityAndLocation from './DebundleQuantityAndLocation';
import DebundleChildSKUAllocator from './DebundleChildSKUAllocator';
import DebundleConfirmation from './DebundleConfirmation';
import { BundleSearchResult } from '../../types';
import { ChildSKUAllocation, DebundleBundleInfo, DebundleState, SourceShelfAllocation } from './types';
import { getAggregatedChildSKUs } from '../../utils/inventoryUtils';

// Re-export types for backwards compatibility
export type { ChildSKUAllocation, DebundleBundleInfo, DebundleState, SourceShelfAllocation } from './types';

const initialState: DebundleState = {
    selectedBundle: null,
    quantity: 1,
    sourceShelfLocation: '',
    sourceShelfAllocations: [],
    childSKUAllocations: [],
    isComplete: false,
};


const workflowSteps = [
    { id: 0, name: 'Select Bundle', description: 'Choose the bundle to debundle' },
    { id: 1, name: 'Quantity & Location', description: 'Set quantity and source shelf' },
    { id: 2, name: 'Allocate Child SKUs', description: 'Choose where to put child SKUs' },
    { id: 3, name: 'Confirm & Execute', description: 'Review and complete debundle' },
];

const DebundleWorkflow: React.FC = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [state, setState] = useState<DebundleState>(initialState);

    const handleBundleSelect = (bundle: BundleSearchResult) => {
        // Convert products array to raw childSKUs format
        const rawChildSKUs = bundle.products.map((product) => ({
            skuId: product,
            name: product,
            requiredQuantity: 1,
        }));

        // Initialize child SKU allocations based on aggregated child SKUs
        const aggregatedChildSKUs = getAggregatedChildSKUs(rawChildSKUs);

        const allocations: ChildSKUAllocation[] = aggregatedChildSKUs.map((childSKU) => ({
            skuId: childSKU.skuId,
            name: childSKU.name,
            totalQuantity: childSKU.requiredQuantity,
            allocations: [],
        }));

        const debundleBundleInfo: DebundleBundleInfo = {
            id: bundle.id,
            name: bundle.name || bundle.bundleSku,
            bundleSku: bundle.bundleSku,
            products: bundle.products,
            childSKUs: aggregatedChildSKUs,
        };

        setState({
            ...state,
            selectedBundle: debundleBundleInfo,
            childSKUAllocations: allocations,
        });
    };

    const handleQuantityChange = (quantity: number, sourceShelfLocation: string, sourceShelfAllocations?: SourceShelfAllocation[]) => {
        // Update child SKU allocations based on new quantity
        const allocations: ChildSKUAllocation[] = state.selectedBundle?.childSKUs.map((childSKU) => ({
            skuId: childSKU.skuId,
            name: childSKU.name,
            totalQuantity: childSKU.requiredQuantity * quantity,
            allocations: [],
        })) || [];

        // Build combined sourceShelfLocation string from allocations for backwards compatibility
        const combinedShelfLocation = sourceShelfAllocations
            ? sourceShelfAllocations.filter(a => a.allocatedQuantity > 0).map(a => `${a.shelfCode}:${a.allocatedQuantity}`).join(', ')
            : sourceShelfLocation;

        setState({
            ...state,
            quantity,
            sourceShelfLocation: combinedShelfLocation,
            sourceShelfAllocations: sourceShelfAllocations || [],
            childSKUAllocations: allocations,
        });
    };

    const handleChildSKUAllocationChange = (allocations: ChildSKUAllocation[]) => {
        setState({
            ...state,
            childSKUAllocations: allocations,
        });
    };

    const handleComplete = () => {
        setState({ ...state, isComplete: true });
    };

    const handleReset = () => {
        setState(initialState);
        setCurrentStep(0);
    };

    const canProceedToNextStep = (): boolean => {
        switch (currentStep) {
            case 0:
                return state.selectedBundle !== null;
            case 1: {
                // Validate total allocated from source shelves equals requested quantity
                const totalAllocated = state.sourceShelfAllocations.reduce((sum, a) => sum + a.allocatedQuantity, 0);
                return state.quantity > 0 && totalAllocated === state.quantity;
            }
            case 2:
                // All child SKUs must have their total quantity allocated
                return state.childSKUAllocations.every((sku) => {
                    const allocated = sku.allocations.reduce((sum, a) => sum + a.quantity, 0);
                    return allocated === sku.totalQuantity;
                });
            case 3:
                return true;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (canProceedToNextStep() && currentStep < workflowSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <DebundleBundleSelector
                        selectedBundle={state.selectedBundle}
                        onBundleSelect={handleBundleSelect}
                    />
                );
            case 1:
                return (
                    <DebundleQuantityAndLocation
                        selectedBundle={state.selectedBundle!}
                        quantity={state.quantity}
                        sourceShelfLocation={state.sourceShelfLocation}
                        onQuantityChange={handleQuantityChange}
                    />
                );
            case 2:
                return (
                    <DebundleChildSKUAllocator
                        selectedBundle={state.selectedBundle!}
                        quantity={state.quantity}
                        allocations={state.childSKUAllocations}
                        onAllocationsChange={handleChildSKUAllocationChange}
                    />
                );
            case 3:
                return (
                    <DebundleConfirmation
                        state={state}
                        onComplete={handleComplete}
                        onReset={handleReset}
                    />
                );
            default:
                return <Typography>Unknown step</Typography>;
        }
    };

    if (state.isComplete) {
        return (
            <PageContainer>
                <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="h6">Debundle Complete!</Typography>
                    <Typography variant="body2">
                        The bundle has been successfully debundled and inventory has been updated.
                    </Typography>
                </Alert>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" onClick={handleReset}>
                        Debundle Another
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/')}>
                        Return to Dashboard
                    </Button>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {/* Header Section */}
            <PageHeader
                icon={<CallSplit />}
                title="Reduce Bundle Inventory"
                subtitle="Break down a bundle into its component child SKUs"
            />

            {/* Progress Stepper */}
            <Card
                sx={{
                    mb: 4,
                    background: '#ffffff',
                    border: '1px solid',
                    borderColor: '#ECECE4',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stepper
                        activeStep={currentStep}
                        alternativeLabel
                        sx={{
                            '& .MuiStepLabel-root': {
                                '& .MuiStepLabel-iconContainer': {
                                    '& .MuiStepIcon-root': {
                                        fontSize: { xs: '1.1rem', sm: '1.6rem', md: '2rem' },
                                        '&.Mui-active': { color: 'primary.main' },
                                        '&.Mui-completed': { color: 'success.main' },
                                    },
                                },
                                '& .MuiStepLabel-labelContainer': {
                                    '& .MuiStepLabel-label': {
                                        fontWeight: 600,
                                        fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.9rem' },
                                        '&.Mui-active': { color: 'primary.main' },
                                        '&.Mui-completed': { color: 'success.main' },
                                    },
                                },
                            },
                        }}
                    >
                        {workflowSteps.map((step) => (
                            <Step key={step.id} completed={currentStep > step.id}>
                                <StepLabel>{step.name}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </CardContent>
            </Card>

            {/* Step Content */}
            <Card
                sx={{
                    background: '#ffffff',
                    border: '1px solid',
                    borderColor: '#ECECE4',
                    boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.05), 0px 2px 4px -1px rgba(0, 0, 0, 0.03)',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, minHeight: { xs: 'auto', md: 450 } }}>
                    <Box className="fade-in">
                        {renderStepContent()}
                    </Box>
                </CardContent>
            </Card>

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    startIcon={<ArrowBack />}
                    sx={{
                        py: 1.25,
                        px: { xs: 2, sm: 3 },
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    Previous
                </Button>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleReset}
                        sx={{
                            py: 1.25,
                            px: { xs: 2, sm: 3 },
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        Reset
                    </Button>

                    {currentStep < workflowSteps.length - 1 && (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canProceedToNextStep()}
                            endIcon={<ArrowForward />}
                            sx={{
                                py: 1.25,
                                px: { xs: 2, sm: 3 },
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: '0px 4px 12px rgba(220, 88, 41, 0.3)',
                                },
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
        </PageContainer>
    );
};

export default DebundleWorkflow;
